import { useRef, useState } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { basketService } from "../BasketService";
import { CompanyNameId, companyIdEquals, isCompanyId, parseNameId, toDisplayName } from "../Company";
import { NaviButtons } from "../components/NaviButtons";
import { withExceptionCapturing } from "../common";

interface Inputs {
	company: string;
}

export const StepCompany = ({
	returnUrl,
	company,
	selectCompany
}: {
	returnUrl: string;
	company?: CompanyNameId;
	selectCompany: (company: CompanyNameId) => Promise<void>;
}) => {
	const { t } = useTranslation();
	const defaultValues = {
		company: company?.displayName
	};
	const {
		register,
		handleSubmit,
		formState: { errors, isSubmitting }
	} = useForm<Inputs>({
		defaultValues: defaultValues
	});
	const onSubmit: SubmitHandler<Inputs> = (data) => selectCompany(parseNameId(data.company));
	const [companyOptions, setCompanyOptions] = useState<string[]>([]);
	const abortRef = useRef<AbortController | undefined>();

	const discontinued = t("companySearchDiscontinued");

	const searchCompanies = async (searchText: string): Promise<string[]> => {
		if (abortRef.current) {
			abortRef.current.abort();
		}

		const abortController = new AbortController();
		abortRef.current = abortController;

		try {
			const companies = await basketService.searchCompanies(searchText, abortRef.current);

			const discontinuedSuffix = " - " + discontinued;
			const options = companies
				.slice(0, 10)
				.map(
					(c) => `${toDisplayName(c.ytunnus, c.yrityksennimi)}${c.yrityksenStatus === "L" ? discontinuedSuffix : ""}`
				);
			setCompanyOptions(options);

			return options;
		} catch (err) {
			if (!abortController.signal.aborted) {
				console.warn(err);
			}
		} finally {
			if (abortController === abortRef.current) {
				abortRef.current = undefined;
			}
		}

		return [];
	};

	const getCompanySearchText = (searchText: string): string => {
		const companyNameId = parseNameId(searchText);
		if (isCompanyId(companyNameId.id)) {
			return companyNameId.id;
		}

		return searchText;
	};

	const onCompanyValueChange = async (e: React.FormEvent<HTMLInputElement>): Promise<void> => {
		let searchText = e.currentTarget.value;
		if (!searchText || searchText.length < 2) {
			return;
		}

		if (companyOptions.find((o) => searchText === o)) {
			// just selected an option from datalist: don't reload options
			return;
		}

		searchText = getCompanySearchText(searchText);

		await searchCompanies(searchText);
	};

	const isSelectedCompanyValid = async (companyValue: string): Promise<string | undefined> => {
		const searchText = getCompanySearchText(companyValue);
		const companies = await basketService.searchCompanies(searchText);
		return !companies.find((o) => companyIdEquals(searchText, o.ytunnus)) ? t("invalidCompanySelection") : undefined;
	};

	return (
		<>
			<div className="row">
				<div className="col-md-12 col-sm-12 col-xs-12">
					<h3>{t("companyBasicInformationTitle")}</h3>
					<form onSubmit={withExceptionCapturing(handleSubmit(onSubmit))}>
						<div className="row">
							<div className="col-md-4 col-sm-6 col-xs-12">
								<div className={`form-group ${errors.company ? "has-error" : ""}`}>
									<label>{t("companySearchLabel")}</label>
									<input
										{...register("company", {
											required: t("requiredField"),
											validate: {
												notDiscontinued: (c) => (c.endsWith(discontinued) ? t("companyDiscontinued") : undefined),
												validSelection: isSelectedCompanyValid
											},
											onChange: withExceptionCapturing(onCompanyValueChange)
										})}
										type="text"
										list="yritykset"
										size={100}
										placeholder={t("companySearchPlaceholder")}
										className="form-control"
										autoComplete="off"
									/>
									<datalist id="yritykset">
										{companyOptions.map((c) => (
											<option value={c}></option>
										))}
									</datalist>
									{errors.company && <div className="help-block">{errors.company.message}</div>}
								</div>
							</div>
						</div>

						<NaviButtons
							backVisible={false}
							cancelText="return"
							cancelVisible
							onCancel={() => (window.location.href = returnUrl)}
							nextText="continue"
							nextDisabled={isSubmitting}
						/>
					</form>
				</div>
			</div>
		</>
	);
};
